import { Component, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { Subscription } from 'rxjs';

import { AppService } from '@app/services/app/app.service';
import { SettingsUserSeatStatus } from '@app/store/state/settings/users/users.state';

import { UsersService } from '@app/services/settings/users.service';
import { User } from '@core/objects/user';
import { HttpStatus } from '@core/classes/http-status.class';
import { HttpRequestStatus } from '@core/classes/http-request-status.class';
import { SeatSetting } from '@core/objects/seat';
import { ThreeDotsMenuItem } from '@core/components/helpers/navigation/three-dots-menu/three-dots-menu-item';

@Component({
    selector: 'settings-users-list-users',
    templateUrl: './list-users.component.html',
    styleUrls: ['./list-users.component.scss']
})
export class SettingsUsersListUsersComponent implements OnInit, OnDestroy{

	public readonly VIEW_MODAL_NONE = 0;
	public readonly VIEW_MODAL_REMOVE_USER_WARNING = 10;
	public readonly VIEW_MODAL_REMOVE_USER_SUCCESS = 20;
	public readonly VIEW_MODAL_REMOVE_USER_ERROR = 30

	// feedback for cancel invitation
	public readonly VIEW_MODAL_CANCEL_INVITATION_WARNING = 11;
	public readonly VIEW_MODAL_CANCEL_INVITATION_SUCCESS = 21;
	public readonly VIEW_MODAL_CANCEL_INVITATION_ERROR = 31

	@Output() reload = new EventEmitter();

	public modalView: number= this.VIEW_MODAL_NONE;

	public usersSeats: SeatSetting[] = null;
	public ownerSeat: SeatSetting = null;
	public loggedUserSeat: SeatSetting = null;
	public seats: Array<SeatSetting> = [];
	public invitations: SeatSetting[] = null;

	public seatsHttpStatus: HttpStatus = null;


	public dataDisabled:string = null;

	public accountOwner: User = null;
	public loggedUser: User = null;
	public seatsAvailable: number = 0;
	public ready: boolean = false;

	public menu: Array<ThreeDotsMenuItem> = [{
		title: "Remove User",
		key: "remove-user",
		icon: "remove-invite",
		order: 1
	}];

	public menuInvitation: Array<ThreeDotsMenuItem> = [{
		title: "Cancel Invitation",
		key: "uninvite",
		icon: "uninvite",
		order: 1
	}];

	// temporal for save the user to remove when action menu is clicked and while modal is on screen
	protected actionSeat: SeatSetting = null;

	protected subs: Subscription = new Subscription();

	constructor(public userSrv: UsersService, public appSrv: AppService){}

	ngOnInit(){
		this.subs.add(this.userSrv.selectorStatusDelUser().subscribe((status: HttpRequestStatus) => {
			switch (status.code){
				case HttpRequestStatus.ERROR:
					this.modalView = this.VIEW_MODAL_REMOVE_USER_ERROR;
					break;
				case HttpRequestStatus.DONE:
					this.modalView = this.VIEW_MODAL_REMOVE_USER_SUCCESS;
					break;
			}
		}));

		this.subs.add(this.userSrv.selectorStatusDelInvite().subscribe((status: HttpRequestStatus) => {
			switch (status.code){
				case HttpRequestStatus.ERROR:
					this.modalView = this.VIEW_MODAL_CANCEL_INVITATION_ERROR;
					break;
				case HttpRequestStatus.DONE:
					this.modalView = this.VIEW_MODAL_CANCEL_INVITATION_SUCCESS;
					break;
			}
		}));

		this.subs.add(this.userSrv.selectorStatusSeatsList().subscribe((status: HttpStatus) => {
			this.seatsHttpStatus = status;
			this.init();
		}));

		this.subs.add(this.userSrv.selectorGetAllSeats().subscribe((seats: SeatSetting[]) => {
			this.seats = seats;
			if(seats != null )
				this.init();

		}));

		this.subs.add(this.appSrv.getSession().subscribe((session) => {
			this.loggedUser = session.current.user;
			this.accountOwner = session.current.account.owner;
			this.init();
		}));

	}

	ngOnDestroy(){
		this.subs.unsubscribe();
	}

	init(){
		if(this.seats == null || this.seatsHttpStatus == null || this.accountOwner == null || this.loggedUser == null)
			return;
		
		if (this.seatsHttpStatus.code == HttpRequestStatus.DONE){  // DONE
			this.setSeatsAvailable();
			// get logged user seat
			let loggedSeats = this.seats.filter((seat: SeatSetting) => seat.status == SettingsUserSeatStatus.USER_SEAT_ASSIGNED &&
					seat.user.email == this.loggedUser.email && 
					seat.user.id == this.loggedUser.id);
			this.loggedUserSeat = loggedSeats.length > 0 ? loggedSeats[0] : null;

			// get account owner seat
			let accountOwnerSeats = this.seats.filter((seat: SeatSetting) => seat.status == SettingsUserSeatStatus.USER_SEAT_ASSIGNED &&
					seat.user.email == this.accountOwner.email && 
					seat.user.id == this.accountOwner.id);
			
			this.ownerSeat = (accountOwnerSeats.length > 0 && accountOwnerSeats[0].user.email != this.loggedUser.email ) ? accountOwnerSeats[0] : null;

			this.usersSeats = this.seats.filter((seat: SeatSetting) => 
					seat.status == SettingsUserSeatStatus.USER_SEAT_ASSIGNED &&
					seat.user.email != this.accountOwner.email && 
					seat.user.id != this.accountOwner.id && 
					seat.user.email != this.loggedUser.email && 
					seat.user.id != this.loggedUser.id 
			);
			this.invitations = this.seats.filter((seat: SeatSetting) => seat.status == SettingsUserSeatStatus.USER_SEAT_PENDING);
			
			this.ready = true;
		}

	}

	tryAgain(){
		this.reload.emit();
	}

	onUserRemove(e){
		this.userSrv.deleteOneUser(this.actionSeat.user.id);
		this.actionSeat = null;
		this.ready = false; // to show loading while reload seats list after remove user action
		this.modalView = this.VIEW_MODAL_NONE;
	}

	onInvitationCancel(e){
		this.userSrv.deleteInvitation(this.actionSeat.user.email);
		this.actionSeat = null;
		this.ready = false; // to show loading while reload seats list after cancel invitation action
		this.modalView = this.VIEW_MODAL_NONE;
	}

	onActionSuccess(e){
		// reset the HttpRequestStatus to INITIAL in the store for remove or cancel invitation
		this.userSrv.clearCancelInvitation(); 
		this.userSrv.clearDeleteUSer(); 
		this.reload.emit();
		this.modalView = this.VIEW_MODAL_NONE;
	}

	dismiss(){
		this.modalView = this.VIEW_MODAL_NONE;
		this.actionSeat = null;
	}

	protected setSeatsAvailable() {
		let total = 0;

		this.seats.forEach(seat => total = total + ( seat.status == SettingsUserSeatStatus.USER_SEAT_FREE ? 1 : 0 ));

		this.seatsAvailable = total;
	}

	onMenuClick(key: string, seat: SeatSetting){
		switch(key) {
			case "remove-user":
					this.actionSeat = seat;
					this.modalView = this.VIEW_MODAL_REMOVE_USER_WARNING;
					break;
		}
	}

	onMenuInvitationClick(key: string, invSeat: SeatSetting ){
		switch(key) {
			case "uninvite":
				this.actionSeat = invSeat;
				this.modalView = this.VIEW_MODAL_CANCEL_INVITATION_WARNING;
				break;
		}
	}

}
